import { ApolloLink, FetchResult, NextLink, Observable, Operation } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'

export interface IOperationCache {
  eTag: string
  response: FetchResult
}

export const ETAG_HEADER_KEY = 'etag'
export const IF_NONE_MATCH_HEADER_KEY = 'if_none_match'
const CACHED_OPERATIONS_KEY = 'cachedOperations'

// Operations whose responses are large and near-static; eligible for ETag caching.
export const ETAG_OPERATIONS = ['clientConfig']

export const buildRequestCacheKey = (operationName: string, digest: string) =>
  `${CACHED_OPERATIONS_KEY}.${operationName}.${digest}`

// cyrb53: small, fast, non-cryptographic string hash. Sufficient for keying a
// handful of GraphQL operations — no crypto guarantees needed.
const cyrb53 = (input: string): string => {
  let high = 0xdeadbeef
  let low = 0x41c6ce57
  for (let index = 0; index < input.length; index++) {
    const charCode = input.charCodeAt(index)
    high = Math.imul(high ^ charCode, 2654435761)
    low = Math.imul(low ^ charCode, 1597334677)
  }
  high = Math.imul(high ^ (high >>> 16), 2246822507) ^ Math.imul(low ^ (low >>> 13), 3266489909)
  low = Math.imul(low ^ (low >>> 16), 2246822507) ^ Math.imul(high ^ (high >>> 13), 3266489909)
  return (4294967296 * (2097151 & low) + (high >>> 0)).toString(16)
}

export const hashOperation = (
  operation: Pick<Operation, 'operationName' | 'query'> & {
    variables?: Record<string, unknown> | null
  }
) =>
  cyrb53(
    JSON.stringify({
      operationName: operation.operationName,
      query: operation.query,
      variables: operation.variables ?? {}
    })
  )

const isOperationCache = (value: unknown): value is IOperationCache =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as IOperationCache).eTag === 'string' &&
  typeof (value as IOperationCache).response === 'object' &&
  (value as IOperationCache).response !== null

export const readCache = (key: string): IOperationCache | undefined => {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return undefined
    const parsed: unknown = JSON.parse(raw)
    // Valid JSON of the wrong shape is treated as a cache miss — avoids sending
    // a bad If-None-Match value or returning a malformed response downstream.
    return isOperationCache(parsed) ? parsed : undefined
  } catch {
    return undefined
  }
}

export const writeCache = (key: string, entry: IOperationCache): void => {
  try {
    localStorage.setItem(key, JSON.stringify(entry))
  } catch {
    // localStorage unavailable or quota exceeded — silently skip caching.
  }
}

// Attaches the stored ETag as an `if_none_match` header for whitelisted
// operations, and stashes the cache entry + key in context for downstream links.
export const requestCacheLink = setContext((operation, previousContext) => {
  const operationName = operation.operationName || ''
  if (!ETAG_OPERATIONS.includes(operationName)) {
    return previousContext
  }

  const requestCacheKey = buildRequestCacheKey(
    operationName,
    hashOperation({
      operationName,
      query: operation.query!,
      variables: operation.variables
    })
  )
  const storedOperationCache = readCache(requestCacheKey)

  return {
    ...previousContext,
    headers: {
      ...previousContext.headers,
      ...(storedOperationCache ? { [IF_NONE_MATCH_HEADER_KEY]: storedOperationCache.eTag } : {})
    },
    storedOperationCache,
    requestCacheKey
  }
})

// On the response: store fresh responses keyed by ETag, and serve the cached
// response when the server's ETag matches what we already hold.
export const afterwareLink = new ApolloLink((operation: Operation, forward: NextLink) =>
  forward(operation).map((response) => {
    const context = operation.getContext()
    const requestCacheKey: string | undefined = context.requestCacheKey
    if (!requestCacheKey) {
      return response
    }

    const storedOperationCache: IOperationCache | undefined = context.storedOperationCache
    const eTag: string | null = context.response?.headers?.get(ETAG_HEADER_KEY) ?? null

    // No ETag header — the server sent a fresh response we cannot cache by ETag.
    // Return it as-is; never fall back to a stale cached entry here.
    if (!eTag) {
      return response
    }

    // New or changed ETag — store and serve the fresh response.
    if (!storedOperationCache || eTag !== storedOperationCache.eTag) {
      writeCache(requestCacheKey, { eTag, response })
      return response
    }

    // ETag unchanged — serve the cached response.
    return storedOperationCache.response
  })
)

// A 304 reply has an empty body, which Apollo surfaces as a network error.
// When that happens, re-emit the cached response so the operation succeeds.
export const etagRecoveryLink = onError(({ networkError, operation }) => {
  if (!networkError || (networkError as { statusCode?: number }).statusCode !== 304) {
    return
  }

  const storedOperationCache: IOperationCache | undefined =
    operation.getContext().storedOperationCache
  if (!storedOperationCache) {
    return
  }

  return new Observable<FetchResult>((observer) => {
    observer.next(storedOperationCache.response)
    observer.complete()
  })
})
